import { useEffect, useRef } from "react";

/**
 * Makes the hardware/gesture Back button close an overlay instead of leaving
 * the page.
 *
 * On a phone, Back is the instinctive way to dismiss a sheet or dialog. Without
 * this, it navigates away from the screen entirely — which on a counter tablet
 * mid-order is destructive.
 *
 * While the overlay is open a history entry is pushed; `popstate` becomes a
 * close. If the overlay is closed some other way (backdrop, X, Escape), the
 * pushed entry is popped on cleanup so the history stack does not accumulate
 * phantom entries that make Back appear to do nothing.
 */
export function useModalBackClose(open: boolean, onClose: () => void): void {
  const closedByPopRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    closedByPopRef.current = false;
    window.history.pushState({ __overlay: true }, "");

    function handlePopState() {
      closedByPopRef.current = true;
      onClose();
    }

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // Only unwind the entry we pushed. If Back is what closed us, the browser
      // has already popped it and calling back() again would navigate away.
      if (!closedByPopRef.current) {
        window.history.back();
      }
    };
  }, [open, onClose]);
}
